import { useEffect } from 'react';
import { resolveNextCueRecalculating } from '@/helpers/resolve-next-cue-recalculating.helper';
import { useNextCueRecalculatingStore } from '@/state/player/next-cue-recalculating.store';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';

/** True while a jump/segment take is awaiting a fresh Next projection from cueState. */
export function useNextCueRecalculating(
  entityRef: string | undefined,
  cueState: FlowCueStateV1 | null | undefined,
): boolean {
  const latch = useNextCueRecalculatingStore((s) => (entityRef ? (s.latchByEntityRef[entityRef] ?? null) : null));
  const mutationFailed = useNextCueRecalculatingStore((s) =>
    entityRef ? Boolean(s.mutationFailedByEntityRef[entityRef]) : false,
  );
  const clearLatch = useNextCueRecalculatingStore((s) => s.clearLatch);

  const resolved = resolveNextCueRecalculating({
    latch,
    cueState,
    mutationFailed,
  });

  useEffect(() => {
    if (!entityRef || !resolved.clearLatch) {
      return;
    }
    clearLatch(entityRef);
  }, [clearLatch, entityRef, resolved.clearLatch]);

  useEffect(() => {
    if (!latch || !entityRef) {
      return;
    }
    const id = window.setInterval(() => {
      // Tick so the watchdog can fire without depending on cue updates.
      useNextCueRecalculatingStore.setState((state) => ({
        latchByEntityRef: { ...state.latchByEntityRef },
      }));
    }, 500);
    return () => window.clearInterval(id);
  }, [entityRef, latch]);

  return resolved.recalculating;
}
